import { useEffect, useMemo, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import qs from 'query-string';

import { COURSE, REGULAR_CLASS } from 'constants/contentTypes';

import {
  initialBundleCart,
  bundleCartAddItem,
  bundleCartRemoveItem,
} from 'reduxModules/bundleCart/action';
import {
  getBundleCartItems,
  getBundleCartInfo,
} from 'reduxModules/bundleCart/selector';

export default function useBundleCart({
  bundleId,
  optionList,
  contentList,
  hashObject,
}) {
  const dispatch = useDispatch();

  const [defaultSelectedItems, setDefaultSelectedItems] = useState([]);

  const { bundleId: currentBundleId } = useSelector(getBundleCartInfo);
  const selectedItems = useSelector(getBundleCartItems);

  const optionCountList = optionList.map(
    ({ requiredOptionsCount }) => requiredOptionsCount,
  );
  const planType = optionList[0]?.plan?.planType;
  const billingCyclePerMonths = optionList[0]?.plan?.billingCyclePerMonths;

  const selectedItemsQueryString = useMemo(() => {
    const groupIdByType = selectedItems.reduce(
      (result, { id, type }) => ({
        ...result,
        [type]: [...(result[type] || []), id],
      }),
      {},
    );

    return qs.stringify(groupIdByType, { arrayFormat: 'comma' });
  }, [selectedItems]);

  const matchedOption = useMemo(
    () =>
      optionList.find(
        option => option.requiredOptionsCount === selectedItems.length,
      ),
    [selectedItems.length, optionList],
  );

  const isAddable = useMemo(
    () => selectedItems.length < Math.max(...optionCountList),
    [selectedItems.length, optionCountList],
  );

  const minimumItemCount = Math.min(...optionCountList);

  const checkIsSelectedItem = useCallback(
    (type, id) =>
      selectedItems.some(
        selectedItem => selectedItem.id === id && selectedItem.type === type,
      ),
    [selectedItems],
  );

  const findItemById = useCallback(
    (type, contentId) => {
      const contentListKeyMap = {
        [COURSE]: 'courses',
        [REGULAR_CLASS]: 'liveTopics',
      };
      const contentTypeKey = contentListKeyMap[type];

      return contentList[contentTypeKey]?.find(
        content => content.id === contentId,
      );
    },
    [contentList],
  );

  const addItem = useCallback(content => dispatch(bundleCartAddItem(content)), [
    dispatch,
  ]);

  const getUnselectedItemsByIds = useCallback(
    (type, ids) =>
      ids
        .filter(contentId => !checkIsSelectedItem(type, contentId))
        .map(contentId => {
          const foundItem = findItemById(type, contentId);
          return (
            foundItem && {
              ...foundItem,
              type,
            }
          );
        })
        .filter(Boolean),
    [checkIsSelectedItem, findItemById],
  );

  useEffect(() => {
    if (currentBundleId !== bundleId) dispatch(initialBundleCart(bundleId));
  }, [dispatch, bundleId, currentBundleId]);

  useEffect(() => {
    if (!hashObject) return;

    const selectedItemsByHash = Object.entries(hashObject).flatMap(
      ([contentType, contentIds]) => {
        const ids = Array.isArray(contentIds) ? contentIds : [contentIds];

        return getUnselectedItemsByIds(contentType, ids);
      },
    );

    if (defaultSelectedItems.length === 0 && selectedItemsByHash.length > 0) {
      setDefaultSelectedItems(selectedItemsByHash);
    }
  }, [hashObject, defaultSelectedItems.length, getUnselectedItemsByIds]);

  useEffect(() => {
    if (defaultSelectedItems.length === 0) return;
    defaultSelectedItems.forEach(item => addItem(item));
  }, [defaultSelectedItems, addItem]);

  const removeItem = ({ type, contentId }) =>
    dispatch(
      bundleCartRemoveItem({
        type,
        contentId,
      }),
    );

  return {
    planType,
    matchedOption,
    selectedItems,
    minimumItemCount,
    billingCyclePerMonths,
    selectedItemsQueryString,
    checkIsSelectedItem,
    isAddable,
    addItem,
    removeItem,
  };
}
